import { ChevronRight } from 'react-feather';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { setServerErrors, useMutation } from '../../Shared/api';
import { useNotifier } from '../../Shared/Notifier/NotificationProvider';
import { Button } from '../../Shared/UI/Button';
import ErrorMessage from '../../Shared/UI/ErrorMessage';
import { Icon } from '../../Shared/UI/Icon';
import { Input } from '../../Shared/UI/Input';
import InputGroup from '../../Shared/UI/InputGroup';
import InputLabel from '../../Shared/UI/InputLabel';
import { InputWrapper } from '../../Shared/UI/InputWrapper';
import ServerErrors from '../../Shared/UI/ServerErrors';
import { useCurrencyInput } from '../../Shared/useCurrencyInput';
import { useRouter } from '../router';

export default function AddOfflineDonationForm({
  campaign,
  onSuccess,
}: {
  campaign: {
    id: string;
  };
  onSuccess?: () => void;
}) {
  const { t } = useTranslation();
  const { routes } = useRouter();
  const { handleCurrencyBlur, parseCurrency } = useCurrencyInput();
  const notifier = useNotifier();

  const { register, handleSubmit, setError, formState: { errors, defaultValues }, setValue, watch } = useForm({
    defaultValues: {
      amount: null as string | null,
    },
  });

  const amount = watch('amount');

  const [addOfflineDonation, { loading, errors: serverErrors }] = useMutation(routes.add_offline_donation(campaign.id), {
    onSuccess: () => {
      notifier.notify(t('backend:offline_donation_added'));
      onSuccess?.();
    },
    onError: (errors) => {
      setServerErrors(errors, setError, defaultValues);
    },
  });

  const submit = async (data: typeof defaultValues) => {
    await addOfflineDonation({
      ...data,
      amount: data?.amount ? parseCurrency(data.amount) : null,
    });
  };

  return (
    <form onSubmit={handleSubmit(submit)}>
      <div className="space-y-6">
        <div className="space-y-2">
          <h3 className="py-1">
            {t('backend:add_offline_donation')}
          </h3>
          <p className="text-slate-700">
            {t('backend:add_offline_donation_intro')}
          </p>
        </div>

        <hr />

        {serverErrors && <ServerErrors errors={serverErrors} defaultValues={defaultValues} />}

        <InputGroup>
          <InputLabel htmlFor="add_offline_donation_amount">
            {t('backend:donation_amount')}
          </InputLabel>
          <InputWrapper>
            <span>
            €
            </span>
            <Input
              {...register('amount')}
              id="add_offline_donation_amount"
              className="pl-7"
              onBlur={(event) => {
                handleCurrencyBlur(event);
                setValue('amount', event.target.value);
              }}
              aria-invalid={!!errors.amount}
              min="0"
              autoFocus
            />
          </InputWrapper>
          <ErrorMessage error={errors.amount} attribute={t('backend:amount')} />
        </InputGroup>

        <hr />

        <div className="text-right">
          <Button
            type="submit"
            disabled={loading || !amount}
            loading={loading}
          >
            {t('backend:continue')}
            <Icon className="ml-1">
              <ChevronRight />
            </Icon>
          </Button>
        </div>
      </div>
    </form>
  );
}
